import { useMemo } from 'react';
import {
  TripDesignerConfigProvider as PackageConfigProvider,
  useTripStore,
} from '@koob/trip-designer';
import { useLanguage, useSnack } from 'hooks';
import { tripFactsheetUrl } from 'utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import search from '@koob/search/src/api';
import { SEARCH_TYPES } from '@koob/search';
import { getTrip, saveTrip } from 'api/trip';
import { getExperience, getExperiences } from 'api/experiences';
import {
  getHotel,
  getHotelsAvailability,
  getRoomsAvailability,
} from 'api/hotels';
import { getThemes } from 'api/themes';
import { getSegments } from 'api/segments';
import { getKinds } from 'api/kinds';
import { getCategories } from 'api/categories';
import { getStyles } from 'api/styles';
import { getSustainableLevels } from 'api/sustainableLevels';
import { getCatalogCountries } from 'api/countries';
import { getPartnerships } from 'api/partnerships';
import {
  getOrganizationByTo,
  getOrganizationByToDmc,
} from 'api/requestConnection';
import { clampDepartureDate } from '@koob/ui/src/features/tripWizard';
import { pushEvent } from '@koob/google-tag-manager';
import { GTM_EVENTS } from '@koob/google-tag-manager/src/events';
import { formatCurrency } from '../utils';

// One-time migration of the pre-US-050 `tripFiltersCache` entry into the
// shared store, for a browser that already had it before this deploy. Runs at
// module load (this file is imported unconditionally, before any route
// renders), not in a `useEffect` — a child route's own mount effect (e.g.
// `Compose/index.js`'s "no tripFilters yet, open the wizard") fires before a
// provider's effects in React's child-then-parent order, so by the time an
// effect here could migrate the cache, that child would already have acted on
// a still-empty store. The key is dropped either way (US-051): nothing writes
// it any more, now that filters persist through the store alone.
const cachedTripFilters = localStorage.getItem('tripFiltersCache');
if (cachedTripFilters && !useTripStore.getState().tripFilters) {
  const filters = JSON.parse(cachedTripFilters);
  if (filters?.departureDate) {
    filters.departureDate = clampDepartureDate(new Date(filters.departureDate));
  }
  useTripStore.getState().setTripFilters(filters);
}
localStorage.removeItem('tripFiltersCache');

// TO's own API functions already resolve the public share token internally
// (see api/shareContext); the package never needs to know about it, so the
// wrapper passes nothing extra through.
const api = {
  getTrip: ({ tripId, versionId }) => getTrip({ tripId, versionId }),
  getExperience: ({ experienceId, locale }) =>
    getExperience({ experienceId, locale }),
  getHotel: ({ hotelId, locale }) => getHotel({ hotelId, locale }),
  // The app function already unwraps the API's { requestId, expiresAt, data }
  // envelope into { requestId, expiresAt, possibilities }; the config still
  // promises { data } to every consumer, so that result becomes the payload.
  getRoomsAvailability: async params => ({
    data: await getRoomsAvailability(params),
  }),
  getHotelsAvailability: params => getHotelsAvailability(params),
  getExperiencesPricing: ({ filters, locale }) =>
    getExperiences({ filters, locale }),
  saveTrip: trip => saveTrip(trip),
  getThemes: () => getThemes(),
  getSegments: () => getSegments(),
  getCategories: () => getCategories(),
  getKinds: () => getKinds(),
  getStyles: params => getStyles(params),
  getSustainableLevels: () => getSustainableLevels({}),
  getOrganizationByToDmc: params => getOrganizationByToDmc(params),
};

// KD-3594 D4 — a rooms refresh (useRoomSelectionRefresh) tells the user only
// when something changed: a warning for a price change, an error when the
// rooms picked are gone. Both apps map the same two event names to a snack
// here rather than in the package, which never imports i18n or @koob/ui.
// US-061/US-064 — the hotel and experience removal handlers moved off a
// direct GTM import onto this same channel; each event still reaches the exact
// GTM call it did before, only routed through the package's generic `onEvent`.
const onTripDesignerEvent = (t, notify) => (name, payload) => {
  if (name === 'rooms.priceChanged') {
    notify(
      t('rooms.priceChanged', {
        hotelName: payload.hotelName,
        previousPrice: formatCurrency({
          amount: payload.previousPrice,
          currency: payload.currency,
        }),
        price: formatCurrency({
          amount: payload.price,
          currency: payload.currency,
        }),
      }),
      { type: 'warning' },
    );
  } else if (name === 'rooms.unavailable') {
    notify(t('rooms.unavailable', { hotelName: payload.hotelName }), {
      type: 'error',
    });
  } else if (name === 'experience.removed') {
    pushEvent(GTM_EVENTS.EXPERIENCE_REMOVED, {
      experience_id: payload.experienceId,
      experience_name: payload.experienceName,
      day_index: payload.dayIndex,
    });
  } else if (name === 'hotel.removed') {
    pushEvent(GTM_EVENTS.HOTEL_REMOVED, {
      hotel_id: payload.hotelId,
      hotel_name: payload.hotelName,
      day_index: payload.dayIndex,
    });
  }
};

// The tour operator picks which DMC operates the trip, so the wizard shows the
// "which DMC" step and its options follow the chosen countries.
const loadDmcOptions = countries =>
  Promise.all([
    getOrganizationByTo({ countries: (countries ?? []).map(c => c.value) }),
    // A partnerships outage just drops the badges, not the DMC list.
    getPartnerships().catch(() => ({ data: [] })),
  ]).then(([orgRes, partnershipRes]) => {
    const enabled = (partnershipRes?.data ?? []).filter(
      p => p.status === 'enabled',
    );
    const sameId = (a, b) => String(a) === String(b);
    return (orgRes?.data ?? []).map(d => ({
      value: d.id,
      label: d.displayName,
      // Name of the *other* partner in each partnership this DMC is in.
      partners: enabled
        .filter(
          p =>
            sameId(p.firstPartner.id, d.id) || sameId(p.secondPartner.id, d.id),
        )
        .map(p =>
          sameId(p.firstPartner.id, d.id)
            ? p.secondPartner.name
            : p.firstPartner.name,
        ),
    }));
  });

const loadCountries = () =>
  getCatalogCountries().then(res =>
    (res?.data ?? []).map(c => ({
      value: c.id,
      label: c.name,
      position: { lat: parseFloat(c.lat), lng: parseFloat(c.lon) },
    })),
  );

const loadCities = (query, locale) =>
  search({ query, types: [SEARCH_TYPES.CITY], locale }).then(res =>
    (res?.data ?? []).map(i => ({ id: i.id, title: i.title, kind: i.type })),
  );

const TripDesignerConfigProvider = ({ children }) => {
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation('tripDesigner');
  const { t: tWizard } = useTranslation('tripWizard');
  const { notify } = useSnack();
  const navigate = useNavigate();

  // Stable: the wizard rebuilds its form config when this changes.
  const designTrip = useMemo(
    () => ({
      t: tWizard,
      dmc: { mode: 'select', loadOptions: loadDmcOptions },
      loadCountries,
      loadCities,
      syncDaysWithDuration: true,
      afterSave: ({ tripId, locale }) => {
        setLang(locale);
        navigate('/trip-designer/compose', { state: { tripId } });
      },
    }),
    [tWizard, setLang, navigate],
  );

  const config = {
    api,
    getFactsheetUrl: experience =>
      experience?.id
        ? tripFactsheetUrl(`/experiences/${experience.id}`, {
            locale: lang,
            // Read when the link is built: subscribing would re-render the
            // whole app on every filter change.
            tripId: useTripStore.getState().tripFilters?.tripId,
          })
        : null,
    locale: lang,
    onEvent: onTripDesignerEvent(t, notify),
    designTrip,
  };

  return (
    <PackageConfigProvider config={config}>{children}</PackageConfigProvider>
  );
};

export default TripDesignerConfigProvider;
